import { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import {
  BulkSheetFilter,
  FilterSelect,
  FilterInput,
  FilterFooter,
} from '../common'
import {
  TextFilter,
  TextFilterOperator,
  TextFilterOperatorLabel,
} from '../TextFilter'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'

type Props = CustomFilterProps & {
  // response.json must be string[]
  fetch: (v: TextFilter) => Promise<APIResponse>
}

const BLANK_OPERATORS = [TextFilterOperator.BLANK, TextFilterOperator.NOT_BLANK]

const buildTextFilterModel = (
  operator: TextFilterOperator,
  value: string
): TextFilter | null => {
  const isBlankOp = BLANK_OPERATORS.includes(operator)
  const isActive = isBlankOp || !!value
  return isActive ? { operator, value: isBlankOp ? '' : value } : null
}

export const ServerSideTextFilter = ({
  model,
  onModelChange,
  api,
  fetch: fetchFn,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive criteria from model (single source of truth)
  const operator =
    (model?.operator as TextFilterOperator) ?? TextFilterOperator.CONTAINS
  const value = (model?.value as string) ?? ''

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [isComposing, setIsComposing] = useState<boolean>(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: TextFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: TextFilter | null) => {
        if (isComposing) return
        if (!v) {
          setFilteredIds(undefined)
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    search(model as TextFilter | null)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as TextFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    afterGuiAttached() {
      inputRef.current?.focus()
    },
  })

  return (
    <BulkSheetFilter>
      <FilterSelect
        variant="outlined"
        value={operator}
        onChange={e =>
          onModelChange(
            buildTextFilterModel(e.target.value as TextFilterOperator, value)
          )
        }
        sx={{ width: '100%' }}
      >
        {/* TODO Use select component to customize style */}
        {Object.values(TextFilterOperator).map(v => (
          <option key={`text-filter-option-${v}`} value={v}>
            {TextFilterOperatorLabel[v]}
          </option>
        ))}
      </FilterSelect>
      <FilterInput
        inputRef={inputRef}
        value={value}
        onChange={e =>
          onModelChange(buildTextFilterModel(operator, e.target.value))
        }
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={e => {
          setIsComposing(false)
          onModelChange(
            buildTextFilterModel(operator, (e.target as HTMLInputElement).value)
          )
        }}
        placeholder={'Filter...'}
        sx={{ height: 22 }}
      />
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
